import { action, thunk, computed } from 'easy-peasy';
import { httpGetPortfolios } from "services/portfolios"
import { reconcileSelection, readStored, writeStored, storageKey, sortPortfolios } from "store/activePortfolios"

// Fields that describe this page load only. The rest of the slice is persisted (store/index.js).
export const PORTFOLIO_NOT_PERSISTED = [
  "loaded", "loadError", "owner", "reconciledKey", "pruned", "activePortfolios", "activeKey",
]

const sameIds = (a, b) => a.length === b.length && a.every((id, i) => id === b[i])

const model = {
  loading: false,
  setLoading: action((state, payload) => {
    state.loading = payload
  }),
  count: 0,
  setCount: action((state, payload) => {
    state.count = payload
  }),
  // My portfolios list
  portfolios: [],
  // The list has been fetched in this page load. The persisted list may be another account's,
  // so the selection is not reconciled against it.
  loaded: false,
  setPortfolios: action((state, payload) => {
    if (payload) {
      state.portfolios = payload
      state.loaded = true
    }
  }),
  // The list request failed. The selector shows it with a retry (#1409); it used to be swallowed.
  loadError: false,
  setLoadError: action((state, payload) => {
    state.loadError = payload
  }),
  getPortfolios: thunk(async (actions, payload) => {
    actions.setLoading(true)
    actions.setLoadError(false)
    return httpGetPortfolios().then(response => {
      if (response.status === 200 && response?.data) {
        actions.setPortfolios(response?.data)
        actions.setCount(response?.data.length)
        // Keeps the selection; "all portfolios" picks up a portfolio created since.
        actions.reconcileActive()
      }
      else {
        actions.setLoadError(true)
      }
    }, error => {
      actions.setLoadError(true)
    }).finally(() => {
      actions.setLoading(false)
    })
  }),
  // Target portfolio: where new records go and what single-portfolio views show.
  // Setting it does not change the active selection.
  portfolio: null,
  setPortfolio: action((state, payload) => {
    state.portfolio = payload
  }),

  // Active portfolios (#1409). See store/activePortfolios.js.
  selectedIds: [],
  allSelected: false,
  // "<userId>:<accountId>" the persisted selection belongs to, so a slice left by another
  // account is not read as this one's.
  selectionKey: null,
  // { key, activePortfolio } of the signed-in user, set by reconcileActive({ myUser }).
  owner: null,
  // Owner the startup reconcile has been done for: it runs once per user and account.
  reconciledKey: null,
  // The stored selection had portfolios that are gone. The selector shows a notice and clears it.
  pruned: false,
  clearPruned: action((state) => {
    state.pruned = false
  }),
  setOwner: action((state, payload) => {
    // Another user or account signed in during this page load: the list in the store is the
    // previous owner's, so wait for the new fetch before reconciling against it (#1409 QA).
    if (state.owner?.key && payload?.key !== state.owner.key) {
      state.loaded = false
    }
    state.owner = payload
  }),
  setReconciled: action((state, { key, pruned }) => {
    state.reconciledKey = key
    state.pruned = pruned
  }),
  setSelection: action((state, { ids, all, target, key }) => {
    // Unchanged values are left alone so views do not fetch again for nothing.
    if (!sameIds(state.selectedIds || [], ids)) {
      state.selectedIds = ids
    }
    state.allSelected = all
    state.selectionKey = key
    if (!target || state.portfolio?.id !== target.id || JSON.stringify(state.portfolio) !== JSON.stringify(target)) {
      state.portfolio = target || null
    }
  }),

  // Name order. An old persisted slice with only `portfolio` reads as [portfolio.id].
  activePortfolios: computed(
    [
      (state) => state.portfolios,
      (state) => state.allSelected,
      (state) => state.selectedIds,
      (state) => (state.selectedIds?.length ? null : state.portfolio?.id),
    ],
    (portfolios, allSelected, selectedIds, legacyId) => {
      const list = sortPortfolios(portfolios)
      if (allSelected) {
        return list
      }
      const ids = selectedIds?.length ? selectedIds : (legacyId ? [legacyId] : [])
      return list.filter((p) => ids.includes(p.id))
    }
  ),
  // Ids of the active portfolios as one string, e.g. "11,12". Views that fetch for the active
  // portfolios depend on this, not on the target portfolio or the array.
  activeKey: computed([(state) => state.activePortfolios], (active) => active.map((p) => p.id).join(",")),

  /**
   * Decide the active selection against the fetched list. Call with { myUser } once the user is
   * loaded (AppContext does); getPortfolios calls it without a payload after every fetch.
   *
   * The first run per user and account takes the stored selection, then the old persisted
   * slice, then myUser.active_portfolio, then the first portfolio. Later runs keep the current
   * selection and only drop portfolios that are gone.
   */
  reconcileActive: thunk((actions, payload, { getState }) => {
    if (payload?.myUser?.id) {
      const { myUser } = payload
      const key = storageKey(myUser.id, myUser.active_account)
      const previous = getState().owner?.key
      actions.setOwner({
        key,
        activePortfolio: myUser.active_portfolio,
      })
      if (previous && previous !== key) {
        // setOwner dropped `loaded`; the new owner's list reconciles when it arrives.
        actions.getPortfolios()
        return null
      }
    }
    const state = getState()
    if (!state.owner || !state.loaded) {
      return null
    }
    const { key, activePortfolio } = state.owner
    const ownerKey = key || "no-account"
    const firstRun = state.reconciledKey !== ownerKey

    const current = (state.selectedIds?.length || state.allSelected || state.portfolio?.id)
      ? {
        ids: state.selectedIds?.length ? state.selectedIds : [state.portfolio?.id],
        all: state.allSelected,
        target: state.portfolio?.id,
      }
      : null
    const stored = firstRun ? readStored(key) : null
    const sliceIsOwners = state.selectionKey == null || state.selectionKey === key
    const saved = stored || (sliceIsOwners ? current : null)

    const result = reconcileSelection(state.portfolios, saved, activePortfolio)
    actions.setSelection({
      ids: result.ids,
      all: result.all,
      target: state.portfolios.find((p) => p.id === result.target) || null,
      key,
    })
    if (firstRun) {
      // Only a stored selection that lost portfolios is worth a notice.
      actions.setReconciled({ key: ownerKey, pruned: Boolean(stored && result.pruned) })
      if (result.ids.length > 0) {
        writeStored(key, result)
      }
    }
    // Later runs change the selection in memory only. A portfolio deleted during the session
    // stays in the stored selection, so the next startup drops it and says so (criterion 18);
    // the next commit from the selector overwrites it anyway.
    return result
  }),

  /**
   * Take a selection into use from the selector: { ids, all, target } with numeric ids.
   * The target falls back to the first selected portfolio. Stored for this user and account.
   * The caller PATCHes myUser.active_portfolio to the returned target, as the old select did.
   */
  commitSelection: thunk((actions, payload, { getState }) => {
    const state = getState()
    const result = reconcileSelection(state.portfolios, payload, null)
    const key = state.owner?.key || null
    const target = state.portfolios.find((p) => p.id === result.target) || null
    actions.setSelection({ ids: result.ids, all: result.all, target, key })
    if (result.ids.length > 0) {
      writeStored(key, result)
    }
    return { ids: result.ids, all: result.all, target }
  }),
};

export default model;
